import { useScheduledEffect } from '@sohanemon/utils/hooks';
import { useQueries } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useRef, useState } from 'react';
import { api } from '@/lib/trpc/client';
import { NoteCard } from './note-card';

const PER_PAGE = 5;

export function NotesList({ tag }: { tag?: string | null }) {
	const [firstPage] = api.note.list.useSuspenseQuery({
		page: 1,
		perPage: PER_PAGE,
		tag: tag ?? undefined,
	});
	const utils = api.useUtils();

	const [pageCount, setPageCount] = useState(1);
	const sentinelRef = useRef<HTMLDivElement>(null);

	const extraPages = useQueries({
		queries: Array.from({ length: pageCount - 1 }, (_, index) => {
			const input = {
				page: index + 2,
				perPage: PER_PAGE,
				tag: tag ?? undefined,
			};
			return {
				queryKey: getQueryKey(api.note.list, input),
				queryFn: () => utils.note.list.fetch(input),
			};
		}),
	});
	const isFetching = extraPages.some((query) => query.isFetching);
	const lastPage = extraPages.at(-1)?.data ?? firstPage;
	const hasMore = (lastPage?.length ?? 0) === PER_PAGE;

	useScheduledEffect(() => {
		const el = sentinelRef.current;
		if (!el || !hasMore) return;
		const observer = new IntersectionObserver(([entry]) => {
			if (entry?.isIntersecting && !isFetching) {
				observer.unobserve(el);
				setPageCount((count) => count + 1);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, isFetching, pageCount]);

	const notes = [
		...(firstPage ?? []),
		...extraPages.flatMap((query) => query.data ?? []),
	];

	return (
		<div className="flex flex-col md:border-secondary md:border-s md:ps-6">
			<div className="flex max-w-3xl flex-col lg:gap-y-16">
				{notes.map((note) => (
					<NoteCard key={note.slug?.current} {...note} page initial="hidden" />
				))}
			</div>
			{isFetching && (
				<div className="flex justify-center py-8">
					<div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
				</div>
			)}
			<div ref={sentinelRef} className="h-4" />
		</div>
	);
}
